import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Collapse, FormGroup, Input, Label, Row } from 'reactstrap';
import { ComboBox, DropdownBox } from '../../../dropdowns';
import moment from 'moment';
import api from "../../../../services/api";
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { FileDropzone, MultipleDropzoneList } from '../../../FileDropzone';
import fileDownload from 'js-file-download';
import ReactStars from 'react-stars';
import { IndeterminateCheckbox } from '../../../react-table/controls';

const columnSizing = {
    sm: 12,
    md: {
        size: 3,
        //offset: 3
    }
}

const lieuNoticeOptions = [
    {lookupId: 0, lookupValue: "No"},
    {lookupId: 1, lookupValue: "Yes"},
];
const apiUrl = "termination";

export default function TerminationBaseForm(props) {
    const {
        reloadManagerState = false,
        shouldReload = true,
        onLoadData,
        id,
        lookups,
        setLookups,
        isApproval,
        itemInEdit,
        setItemInEdit,
        setShouldCheckApprover,
        validationObj,
        setLoading,
        setCanSave,
        isFinal,
        footer,
    } = props;
    const { instance } = useMsal();
    const user = instance.getActiveAccount();

    useEffect(() => {
        if (!reloadManagerState)
            return;

        const apiCalls = [
            api.get(`userprofile/group?username=${user.username}&group=HR`),
            api.get(`userprofile/search/email${user?.username ? `?email=${user?.username ?? ""}` : ""}`),
        ]

        Promise.all(apiCalls)
            .then((responses) => {
                if (!itemInEdit?.managerId)
                    setItemInEdit({
                        ...itemInEdit,
                        manager: responses[1]?.data,
                        managerId: responses[1]?.data?.userProfileId,
                    });
                    
            })
            .catch(error => console.error({error}))
        ;
    }, [reloadManagerState]);

    const loadData = useCallback(() => {
        setLoading(true);
        
        const apiCalls = [
            api.get("lookup/employmenttype"),
            api.get("lookup/recruitmenttype"),
            api.get("lookup/company"),
            api.get("lookup/project"),
            api.get("lookup/position"),
            api.get("lookup/terminationreason"),
            api.get("lookup/terminationresignationreason"),
            api.get("termination/employeeassessmentitems"),
            api.get(`userprofile/search/email${user?.username ? `?email=${user?.username ?? ""}` : ""}`),
        ];

        Promise.all(apiCalls)
            .then((responses) => {
                const newReasons = responses[5].data;
                newReasons.sort((a, b) => a.lookupId - b.lookupId);
                const newResignationReasons = responses[6].data;
                newResignationReasons.sort((a, b) => a.lookupId - b.lookupId);
                const employeeAssessmentItems = responses[7].data;
                const defaultManager = responses[8].data;

                setLookups({
                    employmentTypes: responses[0].data,
                    recruitmentTypes: responses[1].data,
                    companies: responses[2].data,
                    projects: responses[3].data,
                    positions: responses[4].data,
                    reasons: newReasons,
                    resignationReasons: newResignationReasons,
                    employeeAssessmentItems,
                });
                
                if (!id)
                    setItemInEdit({
                        ...itemInEdit,
                        terminationAssessmentItems: employeeAssessmentItems.map(c => {
                            return { employeeAssessmentItemId: c.employeeAssessmentItemId, employeeAssessmentItemName: c.employeeAssessmentItemName, orderIndex: c.orderIndex, assessmentValue: 0, isYesNo: c.isYesNo };
                        }),
                        manager: defaultManager,
                        managerId: defaultManager?.userProfileId,
                    });

                if (onLoadData)
                    onLoadData();
            })
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
    }, [id, user, isApproval, onLoadData, itemInEdit, setItemInEdit]);

    useEffect(() => {
        if (!shouldReload || !reloadManagerState)
            return;

        loadData();
    }, [itemInEdit]);

    useEffect(() => {
        setShouldCheckApprover(true);
    }, [itemInEdit?.projectId]);

    const handleDateChanged = useCallback((value, name) => {
        setItemInEdit({ ...itemInEdit, [name]: value });
    }, [itemInEdit]);

    useEffect(() => {
        if (!itemInEdit?.lastDayEmploymentDate)
            return;

        if (itemInEdit?.lastDayWorkDate)
            return;

        setItemInEdit({...itemInEdit, lastDayWorkDate: itemInEdit.lastDayEmploymentDate})
    }, [itemInEdit?.lastDayEmploymentDate]);

    useEffect(() => {
        setCanSave(
            itemInEdit.candidateId &&
            itemInEdit.projectId &&
            itemInEdit.positionId &&
            itemInEdit.companyId &&
            itemInEdit.recruitmentTypeId &&
            itemInEdit.employmentTypeId &&
            itemInEdit.managerId &&
            itemInEdit.notificationDate &&
            itemInEdit.lastDayWorkDate &&
            itemInEdit.lastDayEmploymentDate &&
            itemInEdit.reasonId &&
            (itemInEdit.reason?.lookupValue !== "Redundancy" || itemInEdit.lieuNotice !== null) &&
            (itemInEdit.reason?.lookupValue !== "Resignation" || (itemInEdit.reasonDetailId && itemInEdit.terminationAttachments?.length)) &&
            (!itemInEdit?.redundancyEntitlements || itemInEdit?.redundancyEntitlementsNotes) &&
            (!itemInEdit?.payOutLeaveBalances || itemInEdit?.payOutLeaveBalancesNotes)
            ?
            true
            :
            false
        );
    }, [itemInEdit]);

    const downloadAttachment = (fileInfo) => {
        if (fileInfo.file) {
            fileDownload(fileInfo.file, fileInfo.file.name);
            return;
        }
        
        setLoading(true);
        const url = `${apiUrl}/attachment/${fileInfo.terminationAttachmentId}`;
        api.get(url, { responseType: 'blob' })
            .then(blob => {
                fileDownload(blob.data, fileInfo.filename);
            }).catch(error => {
                console.error(error)
            }).finally(() => setLoading(false));
    }

    const removeAttachment = useCallback((fileInfo) => {
        const newAttachments = [...itemInEdit.terminationAttachments];
        const removedItem = newAttachments.filter(a => a.terminationAttachmentId === fileInfo.terminationAttachmentId)[0];
        const removedIndex = newAttachments.indexOf(removedItem);
        newAttachments.splice(removedIndex, 1);
        // console.log({removedItem, removedIndex});

        if (removedItem.terminationAttachmentId <= 0) {
            setItemInEdit({ ...itemInEdit, terminationAttachments: [...newAttachments] });
            return;
        }

        const deletedAttachments = itemInEdit.deletedTerminationAttachments && itemInEdit.deletedTerminationAttachments.length ? [...itemInEdit.deletedTerminationAttachments] : [];
        deletedAttachments.push(removedItem.terminationAttachmentId);
        // console.log({deletedAttachments});
        setItemInEdit({ ...itemInEdit, terminationAttachments: [...newAttachments], deletedTerminationAttachments: [...deletedAttachments] });
    }, [itemInEdit]);

    const files = useMemo(() => {
        if (itemInEdit?.terminationAttachments?.length > 0)
            return <MultipleDropzoneList
                idField="terminationAttachmentId"
                nameField="filename"
                sizeField="fileLength"
                download={downloadAttachment}
                remove={removeAttachment}
                data={itemInEdit.terminationAttachments}
            />;

        return null;
    }, [itemInEdit]);

    const assessmentRatingChanged = useCallback((employeeAssessmentItemId, value) => {
        const currentAssessments = JSON.parse(JSON.stringify(itemInEdit?.terminationAssessmentItems ?? []));
        const currentAssessment = currentAssessments.filter(c => c.employeeAssessmentItemId === employeeAssessmentItemId).length ? currentAssessments.filter(c => c.employeeAssessmentItemId === employeeAssessmentItemId)[0] : null;

        if (!currentAssessment)
        {
            const currentLookupEmployeeAssessment = lookups.employeeAssessmentItems.filter(c => c.employeeAssessmentItemId === employeeAssessmentItemId)[0];
            currentAssessments.push({terminationId: itemInEdit?.terminationId ?? 0, employeeAssessmentItemId: employeeAssessmentItemId, employeeAssessmentItemName: currentLookupEmployeeAssessment.employeeAssessmentItemName, orderIndex: currentLookupEmployeeAssessment.orderIndex, assessmentValue: value});
            currentAssessments.sort((a, b) => a.orderIndex - b.orderIndex);
        }

        if (currentAssessment)
        {
            currentAssessment.assessmentValue = value;
            currentAssessment.declineReason = null;
        }

        setItemInEdit({ ...itemInEdit, terminationAssessmentItems: currentAssessments });
    }, [itemInEdit, lookups]);

    const assessmentRatingReasonChanged = useCallback((employeeAssessmentItemId, reason) => {
        const currentAssessments = JSON.parse(JSON.stringify(itemInEdit?.terminationAssessmentItems ?? []));
        const currentAssessment = currentAssessments.filter(c => c.employeeAssessmentItemId === employeeAssessmentItemId).length ? currentAssessments.filter(c => c.employeeAssessmentItemId === employeeAssessmentItemId)[0] : null;

        if (currentAssessment)
            currentAssessment.declineReason = reason;

        setItemInEdit({ ...itemInEdit, terminationAssessmentItems: currentAssessments });
    }, [itemInEdit, lookups]);

    return (
        <>
            <Collapse id="validationAlert" isOpen={validationObj?.length > 0} style={{scrollMargin: "100px"}} className="my-2">
                <Row>
                    <Col sm={12}>
                        <Alert color="warning" className="pa-0 d-flex justify-content-between">
                            <span className="alert-text">
                                <ul className="mb-0">
                                {
                                    validationObj.map((item, index) => <li key={`alert-${index}`}>{item.message}</li>)
                                }
                                </ul>
                            </span>
                        </Alert>
                    </Col>
                </Row>
            </Collapse>
            <Row className="m-0 p-0">
                <Col xs={12} className="m-0 p-0">
                    <Row>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Project
                                    <span className="text-danger">*</span>
                                </Label>
                                <ComboBox
                                    minLength={0}
                                    endpoint="/project/search"
                                    isLookup={false}
                                    idField="projectId"
                                    valueField="projectName"
                                    selectedItem={itemInEdit.project}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.projectId == newItemInEdit?.projectId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, project: item, projectId: item ? item.projectId : null });
                                    }}
                                    disabled={!itemInEdit.candidateId || isFinal}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Position
                                    <span className="text-danger">*</span>
                                </Label>
                                <ComboBox
                                    minLength={0}
                                    endpoint="/position/search"
                                    isLookup={false}
                                    idField="positionId"
                                    valueField="positionName"
                                    selectedItem={itemInEdit.position}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.positionId == newItemInEdit?.positionId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, position: item, positionId: item ? item.positionId : null });
                                    }}
                                    disabled={!itemInEdit.candidateId || isFinal}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Entity
                                    <span className="text-danger">*</span>
                                </Label>
                                <ComboBox
                                    minLength={0}
                                    endpoint="/company/search"
                                    isLookup={false}
                                    idField="companyId"
                                    valueField="companyName"
                                    selectedItem={itemInEdit.company}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.companyId == newItemInEdit?.companyId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, company: item, companyId: item ? item.companyId : null });
                                    }}
                                    disabled={!itemInEdit.candidateId || isFinal}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Recruitment Type
                                    <span className="text-danger">*</span>
                                </Label>
                                <DropdownBox
                                    data={lookups.recruitmentTypes}
                                    selectedItem={itemInEdit.recruitmentType}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.lookupId === newItemInEdit?.recruitmentTypeId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, recruitmentType: item, recruitmentTypeId: item?.lookupId ?? null });
                                    }}
                                    disabled={!itemInEdit.candidateId}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Employment Type
                                    <span className="text-danger">*</span>
                                </Label>
                                <DropdownBox
                                    data={lookups.employmentTypes}
                                    selectedItem={itemInEdit.employmentType}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.lookupId === newItemInEdit?.employmentTypeId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, employmentType: item, employmentTypeId: item?.lookupId ?? null });
                                    }}
                                    disabled={!itemInEdit.candidateId || isFinal}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Manager
                                    <span className="text-danger">*</span>
                                </Label>
                                <ComboBox
                                    minLength={1}
                                    endpoint={`/userprofile/search`}
                                    //accessToken={token?.accessToken}
                                    isLookup={false}
                                    idField="userProfileId"
                                    valueField="nameAndEmail"
                                    selectedItem={itemInEdit.manager}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.userProfileId === newItemInEdit?.managerId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, manager: item, managerId: item?.userProfileId ?? null });
                                    }}
                                    disabled={!itemInEdit.candidateId || isFinal}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Date of Notification
                                    <span className="text-danger">*</span>
                                </Label>
                                <DatePicker
                                    format="dd-MM-yyyy"
                                    weekNumber={true}
                                    onChange={({value}) => {    
                                        handleDateChanged(!value ? null : moment(value).format("YYYY-MM-DDT00:00:00"), "notificationDate");
                                    }}
                                    size="sm"
                                    className="form-control border-0"
                                    value={itemInEdit.notificationDate ? moment(itemInEdit.notificationDate).toDate() : ""}
                                    //min={dateMin}
                                    disabled={itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Last Day of Employment
                                    <span className="text-danger">*</span>
                                </Label>
                                <DatePicker
                                    format="dd-MM-yyyy"
                                    weekNumber={true}
                                    onChange={({value}) => {    
                                        handleDateChanged(!value ? null : moment(value).format("YYYY-MM-DDT00:00:00"), "lastDayEmploymentDate");
                                    }}
                                    size="sm"
                                    className="form-control border-0"
                                    value={itemInEdit.lastDayEmploymentDate ? moment(itemInEdit.lastDayEmploymentDate).toDate() : ""}
                                    //min={itemInEdit?.notificationDate ? moment(itemInEdit.notificationDate).toDate() : null}
                                    disabled={!itemInEdit?.notificationDate || itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Last Day of Working
                                    <span className="text-danger">*</span>
                                </Label>
                                <DatePicker
                                    format="dd-MM-yyyy"
                                    weekNumber={true}
                                    onChange={({value}) => {    
                                        handleDateChanged(!value ? null : moment(value).format("YYYY-MM-DDT00:00:00"), "lastDayWorkDate");
                                    }}
                                    size="sm"
                                    className="form-control border-0"
                                    value={itemInEdit.lastDayWorkDate ? moment(itemInEdit.lastDayWorkDate).toDate() : ""}
                                    //min={itemInEdit?.notificationDate ? moment(itemInEdit.notificationDate).toDate() : null}
                                    disabled={!itemInEdit?.notificationDate || itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Termination Reason
                                    <span className="text-danger">*</span>
                                </Label>
                                <DropdownBox
                                    data={lookups.reasons}
                                    selectedItem={itemInEdit.reason}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.lookupId === newItemInEdit?.reasonId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, reason: item, reasonId: item?.lookupId ?? null, lieuNotice: item?.lookupValue === "Redundancy" ? lieuNoticeOptions[0].lookupId : null, reasonDetail: null, reasonDetailId: null });
                                    }}
                                    disabled={!itemInEdit.candidateId || itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </FormGroup>
                        </Col>
                    {/*{*/}
                    {/*    itemInEdit?.reason?.lookupValue === "Redundancy" ?*/}
                    {/*    <Col {...columnSizing}>*/}
                    {/*        <FormGroup>*/}
                    {/*            <Label>*/}
                    {/*                Pay Notice in Lieu?*/}
                    {/*                <span className="text-danger">*</span>*/}
                    {/*            </Label>*/}
                    {/*            <DropdownBox*/}
                    {/*                data={lieuNoticeOptions}*/}
                    {/*                selectedItem={itemInEdit?.lieuNotice ? lieuNoticeOptions[1] : lieuNoticeOptions[0]}*/}
                    {/*                onChange={(item) => {*/}
                    {/*                    const newItemInEdit = {...itemInEdit};*/}

                    {/*                    if (item?.lookupId === newItemInEdit?.lieuNotice)*/}
                    {/*                        return;*/}

                    {/*                    setItemInEdit({ ...newItemInEdit, lieuNotice: item?.lookupId ?? null });*/}
                    {/*                }}*/}
                    {/*                disabled={itemInEdit?.status?.lookupValue === "Final"}*/}
                    {/*            />*/}
                    {/*        </FormGroup>*/}
                    {/*    </Col>*/}
                    {/*    :*/}
                    {/*    null*/}
                    {/*}*/}
                    {
                        itemInEdit?.reason?.lookupValue === "Resignation" ?
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Resignation Reason
                                    <span className="text-danger">*</span>
                                </Label>
                                <DropdownBox
                                    data={lookups?.resignationReasons}
                                    selectedItem={itemInEdit?.reasonDetail}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.lookupId === newItemInEdit?.reasonDetailId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, reasonDetail: item, reasonDetailId: item?.lookupId ?? null, recruitmentRequestId: null, recruitmentRequest: null, payOutLeaveBalances: false, payOutLeaveBalancesNotes: null });
                                    }}
                                    disabled={itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </FormGroup>
                        </Col>
                        :
                        null
                    }
                    {
                        itemInEdit?.reason?.lookupValue === "Moved to New Project" ?
                        <>
                            <Col {...columnSizing}>
                                <FormGroup>
                                    <Label>
                                        Recruitment Request
                                    </Label>
                                    <ComboBox
                                        minLength={0}
                                        endpoint="/recruitmentrequest"
                                        selectedItem={itemInEdit?.recruitmentRequest}
                                        onChange={(item) => {
                                            setItemInEdit({ ...itemInEdit, recruitmentRequest: item, recruitmentRequestId: item ? item.lookupId : null });
                                        }}
                                        disabled={itemInEdit?.status?.lookupValue === "Final"}
                                    />
                                </FormGroup>
                            </Col>
                            <Col {...columnSizing}>
                                <FormGroup className="h-100 d-flex flex-wrap justify-content-start align-items-center">
                                    <IndeterminateCheckbox
                                        id="payOutLeaveBalances"
                                        type="checkbox"
                                        onChange={(e) => {
                                            const currentValue = e.target.checked;
                                            setItemInEdit({...itemInEdit, payOutLeaveBalances: currentValue, payOutLeaveBalancesNotes: null});
                                        }}
                                        checked={itemInEdit?.payOutLeaveBalances ?? false}
                                        customTitle="Pay Out Leave Balances?"
                                        disabled={itemInEdit?.status?.lookupValue === "Final"}
                                    />
                                </FormGroup>
                            </Col>
                        {
                            itemInEdit?.payOutLeaveBalances ?
                            <Col xs={12}>
                                <Label>
                                    Pay Out Leave Balances Notes
                                    <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    name="payOutLeaveBalancesNotes"
                                    id="payOutLeaveBalancesNotes"
                                    type="textarea"
                                    value={itemInEdit?.payOutLeaveBalancesNotes ?? ""}
                                    rows={6}
                                    onChange={(e) => {
                                        setItemInEdit({...itemInEdit, payOutLeaveBalancesNotes: e.target.value});
                                    }}
                                    disabled={itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </Col>
                            :
                            null
                        }
                        </>
                        :
                        null
                    }
                        <Col xs={12}>
                            <FormGroup>
                                <Label>
                                    Supporting Document
                                {
                                    itemInEdit?.reason?.lookupValue === "Resignation" ?
                                    <span className="text-danger">*</span>
                                    :
                                    null
                                }
                                </Label>
                                <FileDropzone
                                    files={files}
                                    onDrop={([file]) => {
                                        const newAttachments = itemInEdit?.terminationAttachments ? [...itemInEdit.terminationAttachments] : [];
                                        const newIds = newAttachments?.filter(a => a.terminationAttachmentId <= 0);
                                        if (newIds.length)
                                            newIds.sort((a, b) => parseInt(b.terminationAttachmentId) - parseInt(a.terminationAttachmentId));

                                        const newId = newIds.length ? newIds[0].terminationAttachmentId - 1 : 0;
                                        const newData = {
                                            terminationId: itemInEdit.terminationId ?? 0,
                                            terminationAttachmentId: newId,
                                            file
                                        };
                                        newAttachments.push(newData);
                                        // console.log({file, newData, newAttachments});

                                        setItemInEdit({ ...itemInEdit, terminationAttachments: [...newAttachments] });
                                    }}
                                    maxFiles={0}
                                    disabled={itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <IndeterminateCheckbox
                                    id="redundancyEntitlements"
                                    type="checkbox"
                                    labelClassName="job-order-control mr-3"
                                    onChange={(e) => {
                                        const currentValue = e.target.checked;
                                        setItemInEdit({...itemInEdit, redundancyEntitlements: currentValue, redundancyEntitlementsNotes: null});
                                    }}
                                    checked={itemInEdit?.redundancyEntitlements ?? false}
                                    customTitle="Notice in Lieu Payment"
                                    disabled={itemInEdit?.status?.lookupValue === "Final"}
                                />
                            {
                                itemInEdit?.redundancyEntitlements ?
                                <Input
                                    name="redundancyEntitlementsNotes"
                                    id="redundancyEntitlementsNotes"
                                    type="textarea"
                                    value={itemInEdit?.redundancyEntitlementsNotes ?? ""}
                                    rows={6}
                                    onChange={(e) => {
                                        setItemInEdit({...itemInEdit, redundancyEntitlementsNotes: e.target.value});
                                    }}
                                    disabled={itemInEdit?.status?.lookupValue === "Final"}
                                />
                                :
                                null
                            }
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>
                                    <b>Assessment</b>
                                    <span className="text-danger">*</span>
                                </Label>
                                <Row className="m-0 p-0">
                                {
                                    itemInEdit?.terminationAssessmentItems ?
                                    lookups?.employeeAssessmentItems?.filter(c => !c.isYesNo).map((c, idx) => {
                                        const currentAssessmentItem = itemInEdit?.terminationAssessmentItems?.filter(t => t.employeeAssessmentItemId === c.employeeAssessmentItemId)[0];

                                        return (
                                            <Col className="m-0 p-0" key={`assessment_${idx}`} md={12} lg={6}>
                                                <Row className="m-0 p-0">
                                                    <Col className="m-0 p-0" xs={9}>
                                                        <Label>
                                                        {
                                                            currentAssessmentItem?.employeeAssessmentItemName ?? c.employeeAssessmentItemName
                                                        }
                                                        </Label>
                                                    </Col>
                                                    <Col className="m-0 p-0" xs={3}>
                                                        <ReactStars
                                                            className={"stars-on-profile"}
                                                            count={5}
                                                            half={false}
                                                            size={20}
                                                            onChange={(e) => assessmentRatingChanged(c.employeeAssessmentItemId, e)}
                                                            value={currentAssessmentItem?.assessmentValue ?? 0}
                                                            color2={'#ffd700'}
                                                            edit={itemInEdit?.status?.lookupValue !== "Final"}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        );
                                    })
                                    :
                                    null
                                }
                                </Row>
                                <Row className="m-0 mt-2 p-0">
                                {
                                    itemInEdit?.terminationAssessmentItems ?
                                    lookups?.employeeAssessmentItems?.filter(c => c.isYesNo).map((c, idx) => {
                                        const currentAssessmentItem = itemInEdit?.terminationAssessmentItems?.filter(t => t.employeeAssessmentItemId === c.employeeAssessmentItemId)[0];
                                        const selectedItem = currentAssessmentItem && currentAssessmentItem.assessmentValue !== null ? {lookupId: currentAssessmentItem.assessmentValue ? 1 : 0, lookupValue: currentAssessmentItem.assessmentValue ? "Yes" : "No"} : null;

                                        return (
                                            <Col className={`m-0 ${!idx ? "" : "mt-1 "}p-0`} key={`assessment_yes_no_${idx}`} lg={12}>
                                                <Row className="m-0 p-0">
                                                    <Col className="m-0 p-0" md={12} lg={4}>
                                                        <FormGroup>
                                                            <Label>
                                                            {
                                                                currentAssessmentItem?.employeeAssessmentItemName ?? c.employeeAssessmentItemName
                                                            }
                                                            </Label>
                                                            <DropdownBox
                                                                data={[{ lookupId: 1, lookupValue: "Yes" }, { lookupId: 0, lookupValue: "No" }]}
                                                                isLookup={true}
                                                                selectedItem={selectedItem}
                                                                onChange={(item) => assessmentRatingChanged(c.employeeAssessmentItemId, item?.lookupId ?? null)}
                                                                disabled={itemInEdit?.status?.lookupValue === "Final"}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                {
                                                    currentAssessmentItem && currentAssessmentItem?.assessmentValue !== null && !currentAssessmentItem.assessmentValue ?
                                                    <Col className="m-0 p-0" lg={12}>
                                                        <FormGroup>
                                                            <Label>
                                                                Reason
                                                                <span className="text-danger">*</span>
                                                            </Label>
                                                            <Input
                                                                name="declineReason"
                                                                id="declineReason"
                                                                type="textarea"
                                                                value={currentAssessmentItem?.declineReason ?? ""}
                                                                rows={6}
                                                                onChange={(e) => {
                                                                    assessmentRatingReasonChanged(c.employeeAssessmentItemId, e.target.value)
                                                                }}
                                                                disabled={itemInEdit?.status?.lookupValue === "Final"}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    :
                                                    null
                                                }
                                                </Row>
                                            </Col>
                                        );
                                    })
                                    :
                                    null
                                }
                                </Row>
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>
                                    General Notes
                                </Label>
                                <Input
                                    name="generalNotes"
                                    id="generalNotes"
                                    type="textarea"
                                    value={itemInEdit?.generalNotes ?? ""}
                                    rows={6}
                                    onChange={(e) => {
                                        setItemInEdit({...itemInEdit, generalNotes: e.target.value});
                                    }}
                                    disabled={itemInEdit?.status?.lookupValue === "Approved" || itemInEdit?.status?.lookupValue === "Final"}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Col>
            </Row>
        {
            footer
        }
        </>
    );
};